import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lightning, ShieldCheck, Stethoscope, FileText, Warning, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Menú rápido para móvil (lg:hidden). FAB rojo que abre un panel inferior
 * de fondo negro con accesos directos a las acciones más frecuentes.
 * Cada acción solo se muestra si el usuario tiene acceso al módulo (menu-access/me).
 */
const ACTIONS = [
  { id: "epp", labelKey: "quick.newEpp", to: "/seguridad/epp/requests?new=1", Icon: ShieldCheck, menuKey: "epp.requests" },
  { id: "consulta", labelKey: "quick.newConsulta", to: "/salud/consultas?new=1", Icon: Stethoscope, menuKey: "salud.consultas" },
  { id: "permiso", labelKey: "quick.newPermiso", to: "/seguridad/permisos-trabajo?new=1", Icon: FileText, menuKey: "seguridad.permisos" },
  { id: "acto", labelKey: "quick.newActo", to: "/seguridad/actos-condiciones?new=1", Icon: Warning, menuKey: "seguridad.actos_condiciones" },
];

export default function QuickActionsMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [allowedMenu, setAllowedMenu] = useState(null); // null = cargando
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/menu-access/me`, { withCredentials: true })
      .then(({ data }) => setAllowedMenu(new Set(data.allowed || [])))
      .catch(() => setAllowedMenu(new Set()));
  }, []);

  // Mientras carga, muestra todo para evitar parpadeo; luego filtra por acceso.
  const visibleActions = ACTIONS.filter(a => !allowedMenu || allowedMenu.has(a.menuKey));

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  // Sin acciones disponibles para este usuario: no renderiza la barra.
  if (allowedMenu && visibleActions.length === 0) return null;

  return (
    <div>
      {/* Barra inferior de ancho completo (móvil + escritorio) */}
      <button
        onClick={() => setOpen(true)}
        data-testid="quick-actions-fab"
        aria-label={t("quick.title")}
        className="fixed bottom-0 left-0 right-0 z-40 h-14 bg-[var(--brand,#E31E24)] text-white shadow-[0_-4px_16px_rgba(0,0,0,0.3)] flex items-center justify-center gap-3 active:opacity-90 transition-opacity border-t-2 border-white/15"
      >
        <Lightning size={24} weight="fill" />
        <span className="font-black tracking-tight text-base uppercase">{t("quick.title")}</span>
      </button>

      {/* Overlay + bottom sheet */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex flex-col justify-end"
          data-testid="quick-actions-overlay"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div
            className="relative bg-black border-t-4 border-[var(--brand,#E31E24)] rounded-t-2xl p-5 pb-8 animate-in slide-in-from-bottom duration-200"
            data-testid="quick-actions-sheet"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-white text-lg font-black tracking-tight">{t("quick.title")}</h2>
                <p className="text-[11px] text-slate-400 uppercase tracking-widest">{t("quick.subtitle")}</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="quick-actions-close"
                aria-label={t("quick.close")}
                className="w-9 h-9 flex items-center justify-center rounded-sm text-slate-300 hover:bg-zinc-900"
              >
                <X size={22} />
              </button>
            </div>

            <div className="space-y-3">
              {visibleActions.map(({ id, labelKey, to, Icon }) => (
                <button
                  key={id}
                  onClick={() => go(to)}
                  data-testid={`quick-action-${id}`}
                  className="w-full h-14 rounded-lg bg-[var(--brand,#E31E24)] text-white font-bold text-base flex items-center gap-3 px-4 active:scale-[0.98] hover:bg-[var(--brand-dark,#c01a1f)] transition-all shadow-md"
                >
                  <span className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center shrink-0">
                    <Icon size={22} weight="bold" />
                  </span>
                  <span className="text-left leading-tight">{t(labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
